import React, { useState } from 'react';
import {
  ActionIcon,
  Avatar,
  Button,
  Group,
  Loader,
  Text,
  Tooltip,
} from '@mantine/core';
import { IconUser, IconChevronDown } from '@tabler/icons-react';
import { CharacterSelectionModal } from './CharacterSelectionModal';
import type { CharacterSelectorProps } from '../../types/chat';
import { getCharacterImagePath } from '../../lib/character-utils';

export const CharacterSelector = React.memo<CharacterSelectorProps>(({
  selectedCharacter,
  onCharacterSelect,
  onModelSelect,
  models = [],
  characters = [],
  disabled = false,
  isLoading = false,
  isDesktop,
}) => {
  const [modalOpened, setModalOpened] = useState(false);

  const getAvatarContent = (size: number) => {
    if (selectedCharacter === null) {
      return <IconUser size={size * 0.6} />;
    }
    if (selectedCharacter === 'custom') {
      return '✏️';
    }
    const character = characters.find(c => c.id === selectedCharacter);
    if (character) {
      return (
        <Avatar
          src={getCharacterImagePath(character.id)}
          alt={character.name}
          size={size}
        >
          <IconUser size={size * 0.6} />
        </Avatar>
      );
    }
    return <IconUser size={size * 0.6} />;
  };

  const getDisplayText = () => {
    if (selectedCharacter === null) {
      return 'No character';
    }
    if (selectedCharacter === 'custom') {
      return '✏️ Custom prompt';
    }
    const character = characters.find(c => c.id === selectedCharacter);
    return character ? character.name : 'Select Character';
  };

  const getTooltipText = () => {
    if (selectedCharacter === null) {
      return 'No character selected (only for new chats)';
    }
    if (selectedCharacter === 'custom') {
      return 'Using custom system prompt';
    }
    const character = characters.find(c => c.id === selectedCharacter);
    return character ? character.name : 'Select a character';
  };

  if (isLoading) {
    return (
      <Group gap="xs">
        <Loader size="xs" />
        <Text size="xs" c="dimmed">Loading...</Text>
      </Group>
    );
  }

  return (
    <>
      <Tooltip
        label={getTooltipText()}
        position="bottom"
        withArrow
        multiline={!isDesktop}
        maw={isDesktop ? undefined : 250}
      >
        {isDesktop !== false ? (
          <Button
            variant="subtle"
            leftSection={getAvatarContent(20)}
            rightSection={<IconChevronDown size={14} />}
            onClick={() => setModalOpened(true)}
            disabled={disabled}
            size="compact"
            style={{ minWidth: 140, backgroundColor: 'transparent' }}
          >
            <Text size="xs" truncate style={{ flex: 1 }}>
              {getDisplayText()}
            </Text>
          </Button>
        ) : (
          <ActionIcon
            variant="subtle"
            size="sm"
            onClick={() => setModalOpened(true)}
            disabled={disabled}
            aria-label="Select Character"
          >
            {getAvatarContent(24)}
          </ActionIcon>
        )}
      </Tooltip>

      <CharacterSelectionModal
        opened={modalOpened}
        onClose={() => setModalOpened(false)}
        selectedCharacter={selectedCharacter}
        onCharacterSelect={onCharacterSelect}
        onModelSelect={onModelSelect}
        models={models}
        characters={characters}
      />
    </>
  );
});

CharacterSelector.displayName = 'CharacterSelector';